import React, { useState, useEffect, useCallback } from 'react';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  CheckCircle as ProcessedIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  AutoAwesome as GenerateIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  getPayPeriods,
  getPayPeriod,
  getCurrentPayPeriod,
  createPayPeriod,
  generatePayPeriods,
  updatePayPeriod,
  lockPayPeriod,
  unlockPayPeriod,
  processPayPeriod,
  unprocessPayPeriod,
  deletePayPeriod,
  getPayPeriodSettings,
  updatePayPeriodSettings,
  getPayPeriodEmployeeSummary,
} from '../api';
import { useConfirmDialog } from './ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import PayPeriodFormDialog from './dialogs/PayPeriodFormDialog';
import GeneratePeriodsDialog from './dialogs/GeneratePeriodsDialog';
import PayPeriodSettingsDialog from './dialogs/PayPeriodSettingsDialog';
import PeriodDetailsDialog from './dialogs/PeriodDetailsDialog';
import StatusChip from './StatusChip';
import { formatDateOnly } from '../utils/formatDateOnly';

// Format date for display. Gitea #139 (plan 105): 'YYYY-MM-DD' and ISO-midnight strings render the written
// calendar day in every browser zone - no new Date(str) + local getters. Also handed to PeriodDetailsDialog.
const formatDate = formatDateOnly;

// Format date for input
const formatDateInput = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
};


// Period type labels
const periodTypeLabels = {
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  semimonthly: 'Semi-Monthly',
  monthly: 'Monthly',
  custom: 'Custom',
};

export default function PayPeriodManagement() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  // Check if user is admin (can create/edit/delete/lock periods)
  const isAdmin = user?.role === 'admin' || user?.role === 'program_admin';
  const isManager = user?.role === 'manager';
  const canEditSettings = isAdmin || isManager;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, showSuccess, clearSuccess] = useFeedback(3000);
  const [periods, setPeriods] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [settings, setSettings] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodDetails, setPeriodDetails] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    period_type: 'custom',
    notes: '',
  });
  
  const [settingsForm, setSettingsForm] = useState({
    period_type: 'biweekly',
    week_start_day: 0,
    anchor_date: '',
    auto_lock_enabled: false,
    auto_lock_days_after: 3,
    notify_before_lock: true,
    notify_days_before: 2,
    paycheck_day: '',
  });
  
  const [generateForm, setGenerateForm] = useState({
    count: 12,
    start_date: '',
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const [periodsRes, currentRes, settingsRes] = await Promise.all([
        getPayPeriods({ include_stats: 'true' }),
        getCurrentPayPeriod(),
        getPayPeriodSettings(),
      ]);
      
      setPeriods(periodsRes.data || []);
      setCurrentPeriod(currentRes.data?.current || null);
      setSettings(currentRes.data?.settings || settingsRes.data);
      
      // Initialize settings form
      if (settingsRes.data) {
        setSettingsForm({
          period_type: settingsRes.data.period_type || 'biweekly',
          week_start_day: settingsRes.data.week_start_day ?? 0,
          anchor_date: settingsRes.data.anchor_date ? formatDateInput(settingsRes.data.anchor_date) : '',
          auto_lock_enabled: settingsRes.data.auto_lock_enabled || false,
          auto_lock_days_after: settingsRes.data.auto_lock_days_after ?? 3,
          notify_before_lock: settingsRes.data.notify_before_lock ?? true,
          notify_days_before: settingsRes.data.notify_days_before ?? 2,
          paycheck_day: settingsRes.data.paycheck_day ?? '',
        });
      }
    } catch (err) {
      console.error('Error loading pay periods:', err);
      setError('Failed to load pay periods');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle create pay period
  const handleCreate = async () => {
    try {
      setError('');
      await createPayPeriod(formData);
      showSuccess('Pay period created successfully');
      setCreateDialogOpen(false);
      setFormData({ name: '', start_date: '', end_date: '', period_type: 'custom', notes: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create pay period');
    }
  };

  // Handle update pay period
  const handleUpdate = async () => {
    try {
      setError('');
      await updatePayPeriod(selectedPeriod.id, formData);
      showSuccess('Pay period updated successfully');
      setEditDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update pay period');
    }
  };

  // Handle lock/unlock
  const handleLock = async (period) => {
    try {
      setError('');
      await lockPayPeriod(period.id);
      showSuccess(`Pay period "${period.name}" locked`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to lock pay period');
    }
  };

  const handleUnlock = async (period) => {
    try {
      setError('');
      await unlockPayPeriod(period.id);
      showSuccess(`Pay period "${period.name}" unlocked`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlock pay period');
    }
  };

  // Handle process
  const handleProcess = async (period) => {
    const confirmed = await showConfirm({
      title: 'Mark as Processed',
      message: `Mark "${period.name}" as processed?`,
      confirmText: 'Process',
      confirmColor: 'primary',
      severity: 'warning'
    });
    if (!confirmed) return;

    try {
      setError('');
      await processPayPeriod(period.id);
      showSuccess(`Pay period "${period.name}" marked as processed`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process pay period');
    }
  };

  // Handle unprocess (reopen)
  const handleUnprocess = async (period) => {
    const confirmed = await showConfirm({
      title: 'Reopen Pay Period',
      message: `Revert "${period.name}" from processed back to locked? This allows modifications.`,
      confirmText: 'Reopen',
      confirmColor: 'warning',
      severity: 'warning'
    });
    if (!confirmed) return;

    try {
      setError('');
      await unprocessPayPeriod(period.id);
      showSuccess(`Pay period "${period.name}" reopened (locked)`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reopen pay period');
    }
  };

  // Handle delete
  const handleDelete = async (period) => {
    const confirmed = await showConfirm({
      title: 'Delete Pay Period',
      message: `Delete pay period "${period.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;

    try {
      setError('');
      await deletePayPeriod(period.id);
      showSuccess('Pay period deleted');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete pay period');
    }
  };

  // Handle settings save
  const handleSaveSettings = async () => {
    try {
      setError('');
      await updatePayPeriodSettings(settingsForm);
      showSuccess('Settings saved successfully');
      setSettingsDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    }
  };

  // Handle generate periods
  const handleGenerate = async () => {
    try {
      setError('');
      const result = await generatePayPeriods(generateForm);
      showSuccess(`Generated ${result.data.generated} pay periods`);
      setGenerateDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate pay periods');
    }
  };

  // Handle view details
  const handleViewDetails = async (period) => {
    try {
      setError('');
      const [periodRes, summaryRes] = await Promise.all([
        getPayPeriod(period.id),
        getPayPeriodEmployeeSummary(period.id),
      ]);
      setPeriodDetails({
        ...periodRes.data,
        employees: summaryRes.data?.employees || [],
      });
      setDetailsDialogOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load period details');
    }
  };

  // Open edit dialog
  const openEditDialog = (period) => {
    setSelectedPeriod(period);
    setFormData({
      name: period.name || '',
      start_date: formatDateInput(period.start_date),
      end_date: formatDateInput(period.end_date),
      period_type: period.period_type || 'custom',
      notes: period.notes || '',
    });
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>{success}</Alert>}

      {/* Current Period Card */}
      {currentPeriod && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent sx={{ color: 'white' }}>
            <Typography variant="overline" sx={{ opacity: 0.8 }}>Current Pay Period</Typography>
            <Typography variant="h5" fontWeight="bold">{currentPeriod.name}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {formatDate(currentPeriod.start_date)} - {formatDate(currentPeriod.end_date)}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold">
                    {currentPeriod.stats?.entry_count || 0}
                  </Typography>
                  <Typography variant="caption">Entries</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold">
                    {parseFloat(currentPeriod.stats?.total_hours || 0).toFixed(1)}
                  </Typography>
                  <Typography variant="caption">Hours</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold">
                    {currentPeriod.stats?.total_pay == null ? (
                      <Tooltip title="Hidden — needs the View pay rates permission"><span>—</span></Tooltip>
                    ) : (
                      `$${parseFloat(currentPeriod.stats.total_pay).toFixed(0)}`
                    )}
                  </Typography>
                  <Typography variant="caption">Total</Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={currentPeriod.status?.toUpperCase() || 'OPEN'} 
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip 
                label={periodTypeLabels[settings?.period_type] || 'Custom'} 
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isAdmin && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setFormData({ name: '', start_date: '', end_date: '', period_type: settings?.period_type || 'custom', notes: '' });
                    setCreateDialogOpen(true);
                  }}
                >
                  {isMobile ? 'Add' : 'Create Period'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GenerateIcon />}
                  onClick={() => {
                    setGenerateForm({ count: 12, start_date: '' });
                    setGenerateDialogOpen(true);
                  }}
                >
                  {isMobile ? 'Generate' : 'Generate Periods'}
                </Button>
              </>
            )}
            {!isAdmin && (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                View-only mode. Contact an admin to manage pay periods.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEditSettings && (
              <Tooltip title="Settings">
                <IconButton onClick={() => setSettingsDialogOpen(true)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={loadData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Pay Periods List */}
      <TableContainer component={Paper}>
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell>Period</TableCell>
              {!isMobile && <TableCell>Dates</TableCell>}
              <TableCell align="center">Status</TableCell>
              {!isMobile && <TableCell align="right">Entries</TableCell>}
              {!isMobile && <TableCell align="right">Hours</TableCell>}
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No pay periods configured. Create one or generate periods automatically.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              periods.map((period) => (
                <TableRow key={period.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{period.name}</Typography>
                    {isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(period.start_date)} - {formatDate(period.end_date)}
                      </Typography>
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(period.start_date)} - {formatDate(period.end_date)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <StatusChip status={period.status} />
                  </TableCell>
                  {!isMobile && (
                    <TableCell align="right">{period.stats?.entry_count || 0}</TableCell>
                  )}
                  {!isMobile && (
                    <TableCell align="right">{parseFloat(period.stats?.total_hours || 0).toFixed(1)}</TableCell>
                  )}
                  <TableCell align="right">
                    {period.stats?.total_pay == null ? (
                      <Tooltip title="Hidden — needs the View pay rates permission"><span>—</span></Tooltip>
                    ) : (
                      `$${parseFloat(period.stats.total_pay).toFixed(2)}`
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(period)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && period.status === 'open' && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditDialog(period)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Lock Period">
                            <IconButton size="small" color="warning" onClick={() => handleLock(period)}>
                              <LockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(period)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {isAdmin && period.status === 'locked' && (
                        <>
                          <Tooltip title="Unlock Period">
                            <IconButton size="small" color="success" onClick={() => handleUnlock(period)}>
                              <UnlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark as Processed">
                            <IconButton size="small" color="info" onClick={() => handleProcess(period)}>
                              <ProcessedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {isAdmin && period.status === 'processed' && (
                        <Tooltip title="Reopen (revert to locked)">
                          <IconButton size="small" color="warning" onClick={() => handleUnprocess(period)}>
                            <UnlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PayPeriodFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreate}
        mode="create"
        formData={formData}
        setFormData={setFormData}
        periodTypeLabels={periodTypeLabels}
      />

      <PayPeriodFormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleUpdate}
        mode="edit"
        formData={formData}
        setFormData={setFormData}
        periodTypeLabels={periodTypeLabels}
        selectedPeriod={selectedPeriod}
      />

      <GeneratePeriodsDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        onGenerate={handleGenerate}
        generateForm={generateForm}
        setGenerateForm={setGenerateForm}
        periodTypeLabel={periodTypeLabels[settings?.period_type] || 'Bi-Weekly'}
      />

      <PayPeriodSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        onSave={handleSaveSettings}
        settingsForm={settingsForm}
        setSettingsForm={setSettingsForm}
      />

      <PeriodDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        periodDetails={periodDetails}
        formatDate={formatDate}
        periodTypeLabels={periodTypeLabels}
      />

      <ConfirmDialogComponent />
    </Box>
  );
}
